import { Flag, Menu, Timer, LockKeyhole, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MobileMenu } from "./mobileMenu";
import { Link, useRouteLoaderData } from "react-router";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Race, RaceStatus } from "@/backend/domain/models/race";
import { SignedIn, SignedOut, SignInButton, UserButton } from "@clerk/react-router";

type Props = {
    races: Race[];
};

export const getRaceStatusConfig = (status: RaceStatus) => {
    const config = {
        'DRAFT': {
            icon: Flag,
            text: 'Entwurf',
            className: 'text-slate-500'
        },
        'REGISTRATION_OPEN': {
            icon: Timer,
            text: 'Anmeldung offen',
            className: 'text-green-600'
        },
        'REGISTRATION_CLOSED': {
            icon: LockKeyhole,
            text: 'Anmeldung geschlossen',
            className: 'text-amber-600'
        },
        'COMPLETED': {
            icon: CheckCircle2,
            text: 'Abgeschlossen',
            className: 'text-blue-600'
        }
    } as const;

    return config[status];
};

export function MainNavigation({ races }: Props) {
    const [menuOpen, setMenuOpen] = useState(false);
    const rootData = useRouteLoaderData('root') as { isAdmin?: boolean } | undefined;
    const isAdmin = rootData?.isAdmin ?? false;

    return (
        <>
            <nav className="fixed w-full z-40 top-0 px-4 py-4 sm:px-5 sm:py-6 md:px-6 md:py-8 backdrop-blur-sm bg-white/80">
                <div className="max-w-6xl mx-auto flex justify-between items-center">
                    <Link to="/" className="font-light text-xl tracking-widest">
                        SV STANZACH WINTER
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-12 text-sm tracking-wider">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="link"
                                    className="font-light hover:no-underline"
                                >
                                    RENNEN
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="start"
                                className="w-[300px] bg-white/80 backdrop-blur-sm"
                            >
                                {races.length > 0 ? (
                                    races.map((race) => {
                                        const statusConfig = getRaceStatusConfig(race.status);
                                        const StatusIcon = statusConfig.icon;

                                        return (
                                            <DropdownMenuItem key={race.id} asChild>
                                                <Link
                                                    to={`/race/${race.id}`}
                                                    className="flex flex-col space-y-1 py-3 px-4 hover:bg-slate-100 transition-colors"
                                                >
                                                    <span className="font-medium">{race.name}</span>
                                                    <div className="flex items-center gap-2 text-sm">
                            <span className="text-slate-600">
                              {new Date(race.date).toLocaleDateString('de-DE', {
                                  day: 'numeric',
                                  month: 'long'
                              })}
                            </span>
                                                        <span className="text-slate-300">•</span>
                                                        <span className={`flex items-center gap-1 ${statusConfig.className}`}>
                              <StatusIcon className="h-3.5 w-3.5" />
                              <span>{statusConfig.text}</span>
                            </span>
                                                    </div>
                                                </Link>
                                            </DropdownMenuItem>
                                        );
                                    })
                                ) : (
                                    <div className="py-3 px-4 text-sm text-slate-600">
                                        Keine aktiven Rennen
                                    </div>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <SignedIn>
                            <Link to="/myraces">
                                <Button variant="link" className="font-light">MEINE RENNEN</Button>
                            </Link>
                            {isAdmin && (
                                <Link to="/admin">
                                    <Button variant="link" className="font-light">ADMIN</Button>
                                </Link>
                            )}
                        </SignedIn>

                        <SignedOut>
                            <SignInButton mode="modal">
                                <Button variant="outline">
                                    LOGIN
                                </Button>
                            </SignInButton>
                        </SignedOut>

                        <SignedIn>
                            <UserButton afterSignOutUrl="/" />
                        </SignedIn>
                    </div>

                    {/* Mobile Menu Button */}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="md:hidden"
                        onClick={() => setMenuOpen(true)}
                    >
                        <Menu className="h-6 w-6" />
                    </Button>
                </div>
            </nav>

            {/* Mobile Menu */}
            <MobileMenu
                races={races}
                isAdmin={isAdmin}
                isOpen={menuOpen}
                onClose={() => setMenuOpen(false)}
            />
        </>
    );
}
